import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { GroupData } from "../../../../types/GroupData";
import { getSortedGroups } from "../../../../utils/scoreUtils";
import "../../../../assets/styles/common/modal.scss";
import "../../../../assets/styles/common/finalOrder.scss";

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentYear: number;
  type: "final" | "semifinal";
  selectedTab: string;
  onSaved?: () => void;
}

const OrderModal: React.FC<OrderModalProps> = ({
  isOpen,
  onClose,
  currentYear,
  type,
  selectedTab,
  onSaved,
}) => {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [order, setOrder] = useState<GroupData[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchGroups = async () => {
      // entryClass・年（createdAt）はFirestore側の絞り込みに任せ、
      // 全件取得を避ける（GroupEdit.tsxの件数チェックと同じクエリの形）
      const start = new Date(`${currentYear}-01-01T00:00:00`);
      const end = new Date(`${currentYear + 1}-01-01T00:00:00`);

      const groupsQuery = query(
        collection(db, "groups"),
        where("entryClass", "==", selectedTab),
        where("createdAt", ">=", start),
        where("createdAt", "<", end),
      );

      const snapshot = await getDocs(groupsQuery);
      const fetchedGroups: GroupData[] = snapshot.docs.map((doc) => ({
        ...(doc.data() as GroupData),
        id: doc.id,
      }));

      // isDeletedはFirestore側の範囲条件と一緒には使えないためここで絞り込む
      const filtered = fetchedGroups.filter((group) => !group.isDeleted);

      // getSortedGroupsで点数順に並べる
      const scoreSorted = getSortedGroups(filtered, {}, false, [1, 2]);

      let sortedGroups: GroupData[] = [];

      if (type === "final") {
        // finalOrder順
        sortedGroups = [...scoreSorted].sort(
          (a, b) => (a.finalOrder ?? Infinity) - (b.finalOrder ?? Infinity),
        );
      } else {
        // semifinal は entryNumber順
        sortedGroups = [...scoreSorted].sort(
          (a, b) => Number(a.entryNumber) - Number(b.entryNumber),
        );
      }

      setGroups(sortedGroups);
      setOrder(sortedGroups);
    };
    fetchGroups();
  }, [isOpen]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...order];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setOrder(reordered);
  };

  const handleSave = async () => {
    try {
      await Promise.all(
        order.map((group, index) =>
          updateDoc(doc(db, "groups", group.id), {
            [type === "final" ? "finalOrder" : "entryNumber"]: index + 1,
          }),
        ),
      );

      onSaved?.();
      onClose();
    } catch (error) {
      console.error("保存に失敗しました:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2 className="modal-title">
          {type === "final" ? "Final Order" : "Semifinal Order"}
        </h2>
        {type !== "final" ? (
          <p>final Orderは点数ページより操作してください</p>
        ) : (
          <p>Semifinal Orderはエントリーページより操作してください</p>
        )}

        <div className="final-order-modal">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="groups">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {order.map((group, index) => (
                    <Draggable
                      key={group.id}
                      draggableId={group.id}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          className="modal-item"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div>
                            <span className="final-order-number">
                              {index + 1}
                            </span>

                            <span className="final-order-name">
                              {group.groupName}
                            </span>
                          </div>

                          {/* finalは entryNumberを表示 */}
                          {type === "final" && (
                            <span className="modal-item-sub">
                              {group.entryNumber}
                            </span>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            キャンセル
          </button>

          <button onClick={handleSave}>保存する</button>
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
